import { formatterCurrencyTruncated } from '@/utilities/format';
import { toCurrencyTruncated, toTruncated } from '@/utilities/formatting';

import type { NarrativeValuationCompany } from '../hooks/useNarrativeValuationCompany';
import {
  calculateFairValue,
  calculatePriceEarningsMultiple,
  calculateRevenueGrowth,
} from './genericCalculations';
import { ValuatorData } from '../utils';
import { validateIsBetween, INPUT_VALUE_RANGES } from './validation';
import { isNumber } from 'lodash';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { getJsonField } from '@/utilities/object';

const DEFAULT_TIME_PERIOD_IN_YEARS = 5;
const DEFAULT_DISCOUNT_RATE_PERCENTAGE = 10;
const DEFAULT_SHARES_PERCENTAGE = 0.07;

export const getCompanyFinancialData = (company: NarrativeValuationCompany) => {
  const {
    analysisFuture,
    analysisPast,
    analysisValue,
    data,
    analysisMisc,
    primaryIndustry,
    secondaryIndustry,
  } = company;
  const { intrinsicValue, lastSharePrice, pe } = analysisValue ?? {};
  const costOfEquity = getJsonField<number>(
    intrinsicValue,
    'cost_of_equity',
    isNumber
  );
  const { netIncomeMargin, revenue, netIncome } = analysisPast ?? {};
  const { currencyInfo, marketCap } = data;
  const reportingUnit = getJsonField<number>(
    currencyInfo,
    'reporting_unit',
    isNumber
  );
  const sharesOutstanding = getJsonField<number>(
    marketCap,
    'shares_outstanding',
    isNumber
  );
  const { revenueGrowthAnnual, forwardPe1Y } = analysisFuture ?? {};
  const { sharesOutstandingSingleGrowth1Y } = analysisMisc;

  const presentShares = sharesOutstanding !== null ? sharesOutstanding : 0;
  const presentRevenue =
    typeof revenue === 'number' && reportingUnit !== null
      ? revenue * reportingUnit
      : 0;
  const discountRatePercentage = costOfEquity !== null ? costOfEquity * 100 : 0;
  const priceEarningsMultiple = pe !== null ? pe : 0;
  const profitMarginPercentage =
    typeof netIncomeMargin === 'number' ? netIncomeMargin * 100 : 0;
  const revenuePercentage =
    typeof revenueGrowthAnnual === 'number' ? revenueGrowthAnnual * 100 : 0;
  const sharesPercentage =
    sharesOutstandingSingleGrowth1Y !== null
      ? sharesOutstandingSingleGrowth1Y
      : 0;
  const forwardPriceEarningsMultiple1Y = forwardPe1Y !== null ? forwardPe1Y : 0;
  const secondaryIndustryFriendlyName =
    secondaryIndustry?.friendlyName ?? secondaryIndustry?.name;
  const secondaryIndustryAveragesMap = Object.fromEntries(
    secondaryIndustry?.industryAverages?.map(({ name, value }) => [
      name,
      value,
    ]) ?? []
  );

  const primaryIndustryFriendlyName =
    primaryIndustry?.friendlyName ?? primaryIndustry?.name;
  const primaryIndustryAveragesMap = Object.fromEntries(
    primaryIndustry?.industryAverages?.map(({ name, value }) => [
      name,
      value,
    ]) ?? []
  );

  const industryRevenueGrowth =
    secondaryIndustryAveragesMap['future_revenue_growth_3y'];
  const industryPriceEarningMultiple = secondaryIndustryAveragesMap['pe'];
  const secondaryIndustryNetProfitMargin =
    secondaryIndustryAveragesMap['net_income_margin_1y'];
  const primaryIndustryNetProfitMargin =
    primaryIndustryAveragesMap['net_income_margin_1y'];

  const secondaryIndustryNetProfitMarginPercentage =
    secondaryIndustryNetProfitMargin
      ? secondaryIndustryNetProfitMargin * 100
      : 0;
  const primaryIndustryNetProfitMarginPercentage =
    primaryIndustryNetProfitMargin ? primaryIndustryNetProfitMargin * 100 : 0;

  const isInvalidDiscountRatePercentage = !!validateIsBetween(
    discountRatePercentage,
    INPUT_VALUE_RANGES.discountRatePercentage.min,
    INPUT_VALUE_RANGES.discountRatePercentage.max,
    'Discount rate'
  );

  const isInvalidSharesPercentage = !!validateIsBetween(
    sharesPercentage * 100,
    INPUT_VALUE_RANGES.sharesPercentage.min,
    INPUT_VALUE_RANGES.sharesPercentage.max,
    'Shares percentage'
  );
  const cappedSharesPercentage =
    Math.sign(sharesPercentage) === -1
      ? -DEFAULT_SHARES_PERCENTAGE
      : DEFAULT_SHARES_PERCENTAGE;

  const isProfitable = Boolean(netIncome && netIncome > 0);

  return {
    discountRatePercentage: isInvalidDiscountRatePercentage
      ? DEFAULT_DISCOUNT_RATE_PERCENTAGE
      : discountRatePercentage,
    forwardPriceEarningsMultiple1Y,
    industryPriceEarningMultiple,
    industryRevenueGrowth,
    industryNetProfitMarginPercentage:
      secondaryIndustryNetProfitMarginPercentage ||
      primaryIndustryNetProfitMarginPercentage ||
      0,
    presentRevenue,
    presentShares,
    priceEarningsMultiple,
    secondaryIndustryFriendlyName,
    primaryIndustryFriendlyName,
    profitMarginPercentage,
    revenuePercentage,
    sharesPercentage: isInvalidSharesPercentage
      ? cappedSharesPercentage
      : sharesPercentage,
    lastSharePrice,
    isProfitable,
  };
};

export type CalculationOutput = {
  currentYear: number;
  futureYear: number;
  currencyISO: string;
  presentRevenue: number;
  futureRevenue: number;
  futureEarnings: number;
  futureMarketCapitalisation: number;
  futureSharesOutstanding: number;
  discountedMarketCap: number;
  formatted: {
    fairValue: string;
    futureEarnings: string;
    futureYear: string;
    currentYear: string;
    futureMarketCap: string;
    companyDiscountedMarketCap: string;
    futureSharesOutstanding: string;
    sharePriceFutureYear: string;
  };
};

type GetDefaultValuatorDataOptions = {
  presentShares: number;
  presentRevenue: number;
  discountRatePercentage: number;
  priceEarningsMultiple: number | undefined;
  profitMarginPercentage: number;
  revenuePercentage: number;
  sharesPercentage: number;
  industryNetProfitMarginPercentage: number;
  lastSharePrice: number | null | undefined;
  isProfitable: boolean;
  fairValue?: number | null;
  estimateRevenueGrowth?: boolean;
};

export const getDefaultValuatorData = ({
  presentShares,
  presentRevenue,
  discountRatePercentage,
  priceEarningsMultiple,
  profitMarginPercentage,
  revenuePercentage,
  sharesPercentage,
  industryNetProfitMarginPercentage,
  lastSharePrice,
  fairValue,
  isProfitable,
  estimateRevenueGrowth,
}: GetDefaultValuatorDataOptions): ValuatorData => {
  const futureSharesRate = 1 + sharesPercentage;
  const futureSharesOutstanding =
    presentShares * futureSharesRate ** DEFAULT_TIME_PERIOD_IN_YEARS;

  const netProfitMargin = isProfitable
    ? profitMarginPercentage
    : industryNetProfitMarginPercentage;

  const fairValueOrSharePrice = fairValue ?? lastSharePrice ?? 0;

  const estimatedPriceEarningsMultiple = calculatePriceEarningsMultiple({
    discountRatePercentage,
    presentSharePrice: fairValueOrSharePrice,
    futureProfitMarginPercentage: netProfitMargin,
    futureRevenuePercentage: revenuePercentage,
    futureSharesPercentage: sharesPercentage,
    presentRevenue,
    presentShares,
    timePeriod: DEFAULT_TIME_PERIOD_IN_YEARS,
  });

  const targetPriceToEarningsMultiple = isProfitable
    ? priceEarningsMultiple
    : estimatedPriceEarningsMultiple;

  const estimatedFairValue = calculateFairValue({
    discountRatePercentage,
    futurePriceEarningsMultiple: targetPriceToEarningsMultiple ?? 0,
    futureProfitMarginPercentage: netProfitMargin,
    futureRevenuePercentage: revenuePercentage,
    futureSharesPercentage: sharesPercentage,
    presentRevenue,
    presentShares,
    timePeriod: DEFAULT_TIME_PERIOD_IN_YEARS,
  });

  const estimatedRevenuePercentage = calculateRevenueGrowth({
    discountRatePercentage,
    fairValue: fairValueOrSharePrice,
    futurePriceEarningsMultiple: targetPriceToEarningsMultiple ?? 0,
    futureProfitMarginPercentage: netProfitMargin,
    futureSharesPercentage: sharesPercentage,
    presentRevenue,
    presentShares,
    timePeriod: DEFAULT_TIME_PERIOD_IN_YEARS,
  });

  return {
    discountRatePercentage,
    fairValue: fairValue ?? estimatedFairValue,
    futureSharesOutstanding: Math.round(futureSharesOutstanding),
    priceEarningsMultiple: targetPriceToEarningsMultiple ?? 0,
    profitMarginPercentage: netProfitMargin,
    revenuePercentage: estimateRevenueGrowth
      ? estimatedRevenuePercentage
      : revenuePercentage,
    timePeriod: DEFAULT_TIME_PERIOD_IN_YEARS,
  };
};

/**
 * Function: calculateFairValueBreakdown
 * Description: Calculates the fair value breakdown of a company based on input parameters and company data.
 * @param:
 *    @param company {NarrativeValuationCompany} - The company data including analysisPast and data objects.
 *    @param input {ValuatorData} - Input data including fairValue, timePeriod, revenuePercentage, profitMarginPercentage,
 *                                  priceEarningsMultiple, discountRatePercentage and futureSharesOutstanding.
 * @returns {CalculationOutput} - An object containing various calculated values and formatted values for display.
 *      - currentYear: number - The current year.
 *      - futureYear: number - The future year based on timePeriod.
 *      - currencyISO: string - The currency ISO code.
 *      - presentRevenue: number - The present revenue of the company.
 *      - futureRevenue: number - The projected future revenue based on revenuePercentage and timePeriod.
 *      - futureEarnings: number - The projected future earnings based on profitMarginPercentage and futureRevenue.
 *      - futureMarketCapitalisation: number - The projected future market capitalization based on futureEarnings
 *        and priceEarningsMultiple.
 *      - futureSharesOutstanding: number - The projected future shares outstanding based on presentShares,
 *        sharesPercentage, and timePeriod.
 *      - discountedMarketCap: number - The discounted future market capitalization based on discountRatePercentage.
 *      - formatted: object - An object containing formatted versions of some calculated values for display purposes.
 *          - futureMarketCapitalisation: string - Formatted futureMarketCapitalisation value with currency symbol.
 *          - fairValue: string - Formatted fairValue value with currency symbol.
 *          - futureEarnings: string - Formatted futureEarnings value with currency symbol.
 *          - futureYear: string - Formatted futureYear value.
 *          - currentYear: string - Formatted currentYear value.
 *          - futureMarketCap: string - Formatted futureMarketCap value with currency symbol.
 *          - companyDiscountedMarketCap: string - Formatted companyDiscountedMarketCap value with currency symbol.
 *          - futureSharesOutstanding: string - Formatted futureSharesOutstanding value.
 *          - sharePriceFutureYear: string - Formatted sharePriceFutureYear value with currency symbol.
 */
export const calculateFairValueBreakdown = (
  company: NarrativeValuationCompany,
  input: ValuatorData
): CalculationOutput => {
  const {
    discountRatePercentage,
    fairValue,
    futureSharesOutstanding,
    priceEarningsMultiple,
    profitMarginPercentage,
    revenuePercentage,
    timePeriod,
  } = input;

  const { presentRevenue } = getCompanyFinancialData(company);

  const currentYear = new Date().getFullYear();
  const futureYear = currentYear + timePeriod;

  const currencyISO =
    getTradingItemCurrencyISO(company.data.currencyInfo) ?? 'XXX';
  const revenueRate = 1 + revenuePercentage / 100;
  const futureRevenue = presentRevenue * revenueRate ** timePeriod;
  const futureEarnings = (profitMarginPercentage / 100) * futureRevenue;
  const futureMarketCapitalisation = futureEarnings * priceEarningsMultiple;

  const discountRate = discountRatePercentage / 100;
  const discountedMarketCap =
    futureMarketCapitalisation / (1 + discountRate) ** timePeriod;

  const sharePriceFutureYear =
    futureMarketCapitalisation / futureSharesOutstanding;

  // Formatted values
  const fairValueFormatted = toCurrencyTruncated(fairValue, 2, currencyISO);
  const futureEarningsFormatted = toCurrencyTruncated(
    futureEarnings,
    2,
    currencyISO
  );
  const futureYearFormatted = `'${futureYear.toString().slice(-2)}`;
  const currentYearFormatted = `'${currentYear.toString().slice(-2)}`;
  const futureMarketCapFormatted = formatterCurrencyTruncated(
    futureMarketCapitalisation,
    currencyISO
  );
  const companyDiscountedMarketCapFormatted = formatterCurrencyTruncated(
    discountedMarketCap,
    currencyISO
  );

  return {
    currentYear,
    futureYear,
    currencyISO,
    presentRevenue,
    futureRevenue,
    futureEarnings,
    futureMarketCapitalisation,
    futureSharesOutstanding,
    discountedMarketCap,
    formatted: {
      fairValue: fairValueFormatted,
      futureEarnings: futureEarningsFormatted,
      futureYear: futureYearFormatted,
      currentYear: currentYearFormatted,
      futureMarketCap: futureMarketCapFormatted,
      companyDiscountedMarketCap: companyDiscountedMarketCapFormatted,
      futureSharesOutstanding: toTruncated(futureSharesOutstanding),
      sharePriceFutureYear: toCurrencyTruncated(
        sharePriceFutureYear,
        2,
        currencyISO
      ),
    },
  };
};
